import { useLayoutEffect, useMemo, useRef, useState, type ReactElement } from 'react'
import { NavLink, Outlet, useLocation } from 'react-router-dom'
import {
  Briefcase,
  ChevronDown,
  Home,
  LayoutGrid,
  LogOut,
  Users,
  type LucideIcon,
} from 'lucide-react'
import { OwnerCustomerPicker, PortalViewSwitcher } from '@/components/portal-view-switcher'
import { AvatarGradient } from '@/components/ui/avatar'
import { Pill } from '@/components/ui/pill'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { Skeleton } from '@/components/ui/skeleton'
import { ThemeSegmentedControl } from '@/components/theme-toggle'
import { useAuth } from '@/hooks/use-auth'
import { usePortalIdentity } from '@/hooks/use-portal-identity'
import { useSlidingIndicator } from '@/hooks/use-sliding-indicator'
import { useSuperficieCliente } from '@/hooks/use-superficie-cliente'
import { hasPortalViews } from '@/lib/portal-view'
import { PORTAL_HOME } from '@/lib/roles'
import { cn } from '@/lib/utils'

/**
 * PortalLayout — o shell do PORTAL DO CLIENTE (F1; linguagem B desde o Epic 30).
 *
 * Desde o Epic 30 (#425) o cliente tem linguagem própria — a «B · Noturno» que
 * o owner escolheu, com «liberdade total» face ao Lumen. Este shell liga-a
 * (`useSuperficieCliente`, que marca o `<html>` e traz o CSS dela) e desenha a
 * barra dessa linguagem: uma cápsula de vidro que FLUTUA por cima do conteúdo,
 * com uma pílula que desliza até ao destino ativo. No celular a navegação desce
 * para o fundo do ecrã, ao alcance do polegar. Os três planos continuam
 * (substrato → vidro → elevado); o que muda é o material, pelos tokens.
 *
 * Anatomia, como antes: **barra em cima, não trilho ao lado**.
 *
 * Porquê não reutilizar o `AppLayout`: o trilho da Genesis é o mapa do ATS —
 * Candidaturas, Candidatos, Vagas, Clientes, Pipelines. Um cliente não tem
 * nenhum desses destinos, e um trilho de 232px com um item só seria uma
 * moldura vazia a dizer "falta aqui alguma coisa". Um shell horizontal é
 * honesto sobre o tamanho do produto que o cliente vê. (Também há um efeito de
 * bundle: importar o `AppLayout` traria a `AppSidebar` e os sete clientes de
 * listagem que ela consulta para dentro do pacote do cliente, que nunca os vai
 * chamar.)
 *
 * O `--aur-h` não é escrito aqui: a superfície do cliente fixa-o no verde
 * Genesis (`superficie-cliente.css`). O `AppLayout` escreve o dele em linha no
 * `<html>`, mas esse valor nunca chega cá — os dois shells são mutuamente
 * exclusivos por papel, e o único caminho entre eles (sair e entrar) recarrega
 * o documento.
 *
 * Responsive desde o Epic 30: de 360px a 2560px.
 */

interface PortalNavItem {
  id: string
  to: string
  label: string
  icon: LucideIcon
  /**
   * `end` do NavLink: ativo só no caminho EXATO, não nos filhos.
   *
   * Sem opção por omissão de propósito. Um item novo debaixo de `/portal` deixa
   * o "Início" aceso ao mesmo tempo se ele não for `true` — e um trilho com dois
   * itens ativos é um trilho que mente sobre onde se está.
   */
  exact: boolean
}

/**
 * A navegação do portal — TRÊS itens desde a F3, QUATRO desde a Story 38.7 (as
 * Posições). A vitrine entrou por aqui, sem mexer no resto do shell, que foi para isso
 * que a lista existe.
 *
 * ⚠️ Os `exact` são o que impede o trilho de mentir: "Início" é `true` (senão
 * fica aceso em `/portal/vagas` e `/portal/profissionais`, que também começam
 * por `/portal`); os outros são `false`, para continuarem acesos no DETALHE
 * — um trilho que se apaga quando se abre um registo faz a pessoa julgar que
 * saiu da secção.
 *
 * AS PALAVRAS SÃO AS DO CLIENTE (Story 24.2). Eram "Demandas" e "Alocados":
 * palavras de quem fornece — a Genesis recebe demandas e aloca pessoas. Quem lê
 * o portal pensa "a minha vaga" e "os profissionais da Genesis que trabalham
 * comigo". Os caminhos acompanharam (`/portal/vagas`, `/portal/profissionais`),
 * e os antigos redirecionam (`App.tsx`). A API continua em `/api/portal/demands`
 * e `/api/portal/allocations`: é contrato, e o cliente não a lê.
 *
 * A ORDEM É A DO FUNIL, e não a das fases de entrega: primeiro vê-se quem foi
 * apresentado (Vagas), depois os lugares que a Genesis ocupa (Posições), depois quem
 * ficou (Profissionais). Pôr a F2 antes da F3 só porque chegou primeiro faria o trilho
 * contar a história do desenvolvimento em vez da do cliente.
 */
export const PORTAL_NAV: PortalNavItem[] = [
  { id: 'home', to: PORTAL_HOME, label: 'Início', icon: Home, exact: true },
  { id: 'vagas', to: '/portal/vagas', label: 'Vagas', icon: Briefcase, exact: false },
  { id: 'posicoes', to: '/portal/posicoes', label: 'Posições', icon: LayoutGrid, exact: false },
  {
    id: 'profissionais',
    to: '/portal/profissionais',
    label: 'Profissionais',
    icon: Users,
    exact: false,
  },
]

/**
 * Iniciais para o avatar de fallback.
 *
 * ⚠️ DÉBITO HERDADO, declarado e não alargado: existem cinco cópias desta
 * função no repo (`settings.tsx`, `professionals/index.tsx`,
 * `applications/index.tsx`, `dashboard.tsx` e a `useMemo` da `AppSidebar`).
 * Esta é a sexta. Unificá-las é uma varredura de seis ficheiros que não cabe
 * nesta fase — mas fica escrito, porque um desvio herdado que ninguém aponta
 * torna-se permanente.
 */
function initialsOf(name: string | undefined): string {
  const source = (name ?? '').trim()
  if (!source) return '?'
  return (
    source
      .split(/\s+/)
      .map((part) => part[0])
      .filter(Boolean)
      .slice(0, 2)
      .join('')
      .toUpperCase() || '?'
  )
}

/**
 * O marcador do inquilino: logotipo (quando existe) + nome do cliente.
 *
 * É CHROME, na escala do wordmark "Caliber" do trilho interno (15px) — não é
 * um título. Quem dá o nome ao inquilino em tamanho de conteúdo é a tela.
 *
 * Em erro NÃO grita: mostra "Portal do cliente" e cala-se. A tela por baixo é
 * que reporta a falha, com a ação certa; um segundo alarme na barra seria a
 * mesma notícia dita duas vezes.
 */
/** O rótulo da vista da Genesis no cabeçalho (issue #584; Story 35.9). */
const MODO_DA_VISTA = {
  member: 'Perfil de cliente',
  account_manager: 'Gestor de conta',
  owner: 'Owner',
} as const

function TenantMark(): ReactElement {
  const { data, isPending } = usePortalIdentity()

  return (
    <div className="flex min-w-0 items-center gap-2.5">
      {data?.customer.logoUrl ? (
        // `alt=""` + `aria-hidden`: o nome do cliente está ao lado, em texto.
        // Um alt próprio fá-lo-ia ler duas vezes.
        <img
          src={data.customer.logoUrl}
          alt=""
          aria-hidden="true"
          className="border-hair size-7 shrink-0 rounded-md border object-contain"
        />
      ) : null}

      {isPending ? (
        <Skeleton className="h-4 w-36" />
      ) : (
        <span
          className="cl-marca text-ink truncate font-semibold"
          style={{ fontSize: 15, letterSpacing: '-0.018em' }}
        >
          {data?.customer.name ?? 'Portal do cliente'}
        </span>
      )}

      {/* Issue #584 — a pessoa da Genesis vê em que vista está: como gestora de conta,
          ou como owner (Story 35.10), age como o cliente, e isso tem de estar à vista.
          Neutra: é uma seleção, não um estado. */}
      {data?.mode === 'account_manager' || data?.mode === 'member' || data?.mode === 'owner' ? (
        <Pill className="shrink-0">{MODO_DA_VISTA[data.mode]}</Pill>
      ) : null}
    </div>
  )
}

/**
 * O menu do usuário — o mesmo material do rodapé da sidebar interna (Popover
 * de vidro), na anatomia de uma barra: abre para BAIXO e ancora à direita.
 *
 * Sem "Configurações": `/settings` é uma tela interna, e o `ProtectedRoute`
 * devolveria o cliente a `/portal` no instante seguinte. Um item de menu que
 * não vai a lado nenhum é pior que a sua ausência.
 *
 * COM o controlo de tema: não é funcionalidade nova — é a única superfície do
 * produto onde ele vive, e ela é a sidebar, que o cliente nunca vê. Sem isto o
 * cliente ficaria preso ao tema que o sistema operativo lhe deu, sem forma de
 * o mudar.
 */
function PortalUserMenu(): ReactElement {
  const { user, signOut } = useAuth()
  const { data } = usePortalIdentity()
  const [open, setOpen] = useState(false)
  // A página do menu (#736), como no rodapé da sidebar interna: volta a `'menu'` ao
  // ABRIR, porque fechar nem sempre passa pelo `onOpenChange` (o «Sair», a troca de
  // vista) e o conteúdo continua montado durante a saída.
  const [pagina, setPagina] = useState<'menu' | 'clientes' | 'voltou'>('menu')
  function mudarAberto(aberto: boolean): void {
    if (aberto) setPagina('menu')
    setOpen(aberto)
  }

  // `/api/portal/me` é a fonte preferida (é o contrato do portal); `useAuth` é
  // a rede de segurança enquanto ele não resolve, para o menu nunca aparecer
  // sem nome.
  const name = data?.user.name ?? user?.name ?? 'Convidado'
  const email = data?.user.email ?? user?.email ?? 'sem sessão'
  const avatarSrc = data?.user.avatarUrl ?? null
  const initials = useMemo(() => initialsOf(name), [name])
  // A vista da aba, para o «Ver como» e para a lista do owner. O `customer` é o
  // próprio cliente: não há vista da Genesis a marcar.
  const vistaAtual =
    data && data.mode !== 'customer'
      ? { customerId: data.customer.id, customerName: data.customer.name, mode: data.mode }
      : null

  return (
    <Popover open={open} onOpenChange={mudarAberto}>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-haspopup="dialog"
          aria-expanded={open}
          // WCAG 2.5.3 (Label in Name): o nome acessível contém TODO o texto
          // visível do botão — que aqui é o nome do usuário.
          aria-label={`Menu do usuário: ${name}`}
          className={cn(
            'flex max-w-[240px] items-center gap-2.5 rounded-full border border-transparent py-1 pl-1 pr-2.5',
            'transition-colors duration-150 ease-out',
            'hover:bg-sunk hover:border-hair',
            open && 'bg-sunk border-hair',
          )}
        >
          <AvatarGradient initials={initials} src={avatarSrc} size={28} />
          <span className="text-ink truncate text-[12.5px] font-medium">{name}</span>
          <ChevronDown
            className={cn(
              'text-ink-3 size-4 shrink-0 transition-transform duration-200',
              open ? 'rotate-180' : 'rotate-0',
            )}
            aria-hidden="true"
          />
        </button>
      </PopoverTrigger>

      <PopoverContent side="bottom" align="end" sideOffset={8} className="w-75 p-2">
        {pagina === 'clientes' ? (
          <OwnerCustomerPicker
            current={vistaAtual}
            onBack={() => setPagina('voltou')}
            onNavigate={() => setOpen(false)}
          />
        ) : (
          <>
            <div className="flex items-center gap-2.5 px-1.5 pb-2 pt-1">
              <AvatarGradient initials={initials} src={avatarSrc} size={32} />
              <div className="min-w-0 leading-tight">
                <p className="text-ink truncate text-[13px] font-semibold">{name}</p>
                <p className="text-ink-3 truncate text-[11px]">{email}</p>
              </div>
            </div>

            <div className="border-hair my-1.5 h-px" />

            {/* Clicar num segmento NÃO fecha o popover — é por isso que o material
                é um Popover e não um DropdownMenu (ver o cabeçalho de popover.tsx). */}
            <p className="eyebrow px-1.5 pb-1.5 pt-0.5">Tema</p>
            <div className="px-1.5 pb-1.5">
              <ThemeSegmentedControl compact />
            </div>

            <div className="border-hair my-1.5 h-px" />

            {/* Issue #584 — a pessoa da Genesis numa vista volta por aqui, ou troca. */}
            {hasPortalViews(user?.portalViews) ? (
              <>
                <PortalViewSwitcher
                  views={user.portalViews}
                  current={vistaAtual}
                  onNavigate={() => setOpen(false)}
                  onOpenCustomerPicker={() => setPagina('clientes')}
                  returnFocus={pagina === 'voltou'}
                />
                <div className="border-hair my-1.5 h-px" />
              </>
            ) : null}

            <button
              type="button"
              onClick={() => {
                setOpen(false)
                void signOut()
              }}
              className="text-error hover:bg-sunk flex w-full items-center gap-2.5 rounded-sm px-2.5 py-2 text-[13px] font-medium transition-colors"
            >
              <LogOut className="size-4 shrink-0" aria-hidden="true" />
              Sair
            </button>
          </>
        )}
      </PopoverContent>
    </Popover>
  )
}

/**
 * A navegação do portal, com a pílula que desliza até ao destino ativo.
 *
 * A pílula é o `useSlidingIndicator` (o mesmo que move o traço do trilho
 * interno): mede o link com `aria-current="page"` e a pílula vai até lá. O
 * estado ativo continua a ser o `aria-current` que o NavLink escreve — a pílula
 * só o DESENHA, por isso não pode mentir sobre onde se está. Numa rota fora do
 * menu nenhum link está ativo e a pílula sai, em vez de ficar no anterior.
 *
 * ⚠️ `relative` é o que faz das medidas do indicador (offsetLeft/Top) medidas
 * dentro DESTA lista. No celular a lista é `fixed` (ver `superficie-cliente.css`),
 * que também é um ancestral posicionado — a pílula segue-a sem mudar nada.
 */
function PortalNav(): ReactElement {
  const { pathname } = useLocation()
  const { containerRef, box } = useSlidingIndicator<HTMLElement>('a[aria-current="page"]', pathname)

  return (
    // `max-md:glass`: no celular a lista é ela própria a barra de vidro do fundo;
    // a partir de `md` fica dentro da cápsula, que já é vidro.
    <nav
      ref={containerRef}
      aria-label="Navegação do portal"
      className="cl-nav max-md:glass relative flex items-center gap-1"
    >
      {box ? (
        <span
          aria-hidden="true"
          className="cl-pilula left-0 top-0"
          style={{
            transform: `translate(${box.left}px, ${box.top}px)`,
            width: box.width,
            height: box.height,
          }}
        />
      ) : null}
      {PORTAL_NAV.map(({ id, to, label, icon: Icon, exact }) => (
        <NavLink key={id} to={to} end={exact} className="cl-nav-link">
          <Icon className="size-[17px] shrink-0" aria-hidden="true" />
          <span className="truncate">{label}</span>
        </NavLink>
      ))}
    </nav>
  )
}

export function PortalLayout(): ReactElement {
  useSuperficieCliente()
  const { pathname } = useLocation()
  const mainRef = useRef<HTMLElement>(null)

  // CADA ROTA COMEÇA NO TOPO (Story 30.7). O `<main>` é o scroller e persiste
  // entre rotas: sem isto, a página nova abria à altura a que a anterior estava
  // rolada — e o destino de uma transição entre rotas (`lib/transicao-de-rota.ts`)
  // ficava fora da tela. De layout, e não passivo: corre antes da pintura e
  // antes da fotografia do "depois" da transição.
  useLayoutEffect(() => {
    mainRef.current?.scrollTo?.({ top: 0, behavior: 'instant' })
  }, [pathname])

  return (
    <>
      {/* plano 1 — o mesmo substrato do shell interno; a superfície do cliente
          tinge a aurora de verde Genesis */}
      {/* NA IMPRESSÃO (Story 34.7) sai só o conteúdo: sem aurora, sem grão, sem a
          barra, e com o `<main>` a crescer em vez de rolar — um scroller de altura
          fixa imprimia só a parte visível da tela. */}
      <div className="substrate print:hidden" aria-hidden="true" />
      <div className="grain print:hidden" aria-hidden="true" />

      {/* `h-dvh`, e não `h-screen`: no celular o `100vh` é a janela GRANDE (sem a
          barra do browser), e o fundo do conteúdo ficava por baixo dela. */}
      <div className="relative z-[2] flex h-dvh flex-col print:block print:h-auto">
        {/* plano 2 — vidro. A barra é chrome, o único plano onde o
            `backdrop-filter` é permitido. Flutua POR CIMA do conteúdo (é
            `absolute`, e o `<main>` reserva-lhe o espaço no topo), para o vidro
            desfocar o que passa por baixo ao rolar.
            ⚠️ `md:glass`, e não `glass`: no celular a navegação é `fixed` no
            fundo do ecrã, e um ancestral com `backdrop-filter` passaria a ser o
            containing block dela — ficava presa à barra.
            ⚠️ A POSIÇÃO vive no `.cl-barra` (superficie-cliente.css), e não em
            classes do Tailwind aqui: a `@utility glass` declara
            `position: relative`, e no `md:` ganharia a um `absolute`. */}
        <header className="cl-barra md:glass flex items-center gap-3 px-4 py-2 md:gap-6 md:pl-5 md:pr-2 print:hidden">
          <TenantMark />
          <PortalNav />
          <div className="ml-auto">
            <PortalUserMenu />
          </div>
        </header>

        {/* plano 3 — o conteúdo. Quem faz scroll é ESTA área, nunca o body: é o
            que mantém a barra fixa. O `relative` fecha essa promessa — sem ele,
            um `position: absolute` de dentro (o `<select>` escondido que o Radix
            Select renderiza, por exemplo) ancoraria no ancestral posicionado
            mais acima e injetaria a sua altura num SEGUNDO scroll, o do
            documento. Diagnóstico completo em `app-layout.tsx`.
            O espaço de cima é o da barra flutuante; o de baixo, no celular, o da
            navegação no fundo do ecrã. O MESMO espaço vai no `scroll-padding`:
            sem ele, o foco do teclado e o `scrollIntoView` (o título do perfil
            aberto na vitrine) paravam por baixo da barra — WCAG 2.4.11.
            `isolate`: o conteúdo pinta numa camada própria, por baixo da barra,
            sem depender de um efeito colateral (a seta da faixa é `z-10`). */}
        <main
          ref={mainRef}
          className="relative isolate min-h-0 flex-1 scroll-pb-28 scroll-pt-14 overflow-y-auto overflow-x-hidden pb-28 pt-14 md:scroll-pb-0 md:scroll-pt-20 md:pb-0 md:pt-20 print:overflow-visible print:p-0"
        >
          {/* A tela entra quando a ROTA muda — a chave remonta o contentor e a
              animação de CSS corre uma vez, não a cada render. */}
          <div key={pathname} className="cl-entrada">
            <Outlet />
          </div>
        </main>
      </div>
    </>
  )
}

export default PortalLayout
